import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Anchor, Globe, Users, TrendingUp, Award, CheckCircle, Mail, Briefcase, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const Careers = () => {
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    location: '',
    resume: '',
    coverLetter: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const apiUrl = `${BACKEND_URL}/api/careers`;
      
      console.log('=== Career Form Submission Debug ===');
      console.log('Backend URL:', BACKEND_URL);
      console.log('API URL:', apiUrl);
      console.log('Form Data:', formData);
      
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        experience: formData.experience,
        location: formData.location,
        resume: formData.resume || null,
        cover_letter: formData.coverLetter
      };
      
      console.log('Payload:', payload);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error text:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.success) {
        toast.success(data.message);
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          location: '',
          resume: '',
          coverLetter: ''
        });
      } else {
        toast.error(data.message || 'Failed to submit application');
      }
    } catch (error) {
      console.error('=== ERROR submitting application ===');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Full error:', error);
      toast.error(`Failed to submit: ${error.message}`);
    } finally {
      setFormLoading(false);
    }
  };

  const benefits = [
    {
      icon: Globe,
      title: "Global Opportunities",
      description: "Work across New Zealand, Australia, Pacific Islands, and India on diverse maritime projects"
    },
    {
      icon: TrendingUp,
      title: "Career Growth",
      description: "Professional development programs and clear pathways for advancement in marine surveying"
    },
    {
      icon: Award,
      title: "Industry Recognition",
      description: "Work with an approved National Inspection Agency and gain recognized qualifications"
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Learn from experienced professionals with 25+ years of combined maritime expertise"
    },
    {
      icon: Briefcase,
      title: "Competitive Package",
      description: "Attractive compensation, benefits, and opportunities for international assignments"
    },
    {
      icon: GraduationCap,
      title: "Training & Development",
      description: "Access to Gamma Marine Training Institute programs and industry certifications"
    }
  ];

  const positions = [
    {
      title: "Marine Surveyor",
      type: "Full-time",
      location: "Tauranga, NZ / Field-based",
      description: "Experienced marine surveyor to conduct vessel inspections, cargo surveys, and technical assessments across our service regions."
    },
    {
      title: "Junior Marine Surveyor",
      type: "Full-time",
      location: "Tauranga, NZ",
      description: "Entry-level position for maritime graduates or junior professionals seeking to develop expertise in marine surveying under experienced mentorship."
    },
    {
      title: "Technical Inspector",
      type: "Full-time",
      location: "Various Locations",
      description: "Conduct lifting gear inspections, cargo lashing verification, and equipment compliance assessments at ports and vessels."
    },
    {
      title: "Marine Engineering Consultant",
      type: "Contract / Full-time",
      location: "Remote / Field-based",
      description: "Provide technical assistance and consultation on vessel operations, maintenance programs, and marine engineering solutions."
    }
  ];

  const requirements = [
    "Maritime engineering or naval architecture qualifications",
    "Experience in vessel operations, ship management, or marine surveying",
    "Strong understanding of maritime regulations and industry standards",
    "Excellent report writing and communication skills",
    "Ability to travel regionally and internationally",
    "Physical fitness for shipboard inspections",
    "Valid maritime certifications (preferred)"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-maritime-primary">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1618317497044-cf78ada06f92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxoYXJib3J8ZW58MHx8fHwxNzcxMjAyMTk1fDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-maritime-accent/20 backdrop-blur-sm border border-maritime-accent/30 rounded-full px-4 py-2 mb-6">
              <Anchor className="w-4 h-4 text-maritime-accent" />
              <span className="text-sm text-white font-medium">Career Opportunities</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6">Join Our Crew</h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Build your career with New Zealand's approved National Inspection Agency. 
              Join a team of maritime professionals delivering excellence across the Pacific region.
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-maritime-primary mb-4">
              Why Work With Gamma Marine
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We offer more than just a job – we provide a pathway to a rewarding career 
              in the dynamic maritime industry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-2 border-transparent hover:border-maritime-accent/20 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-maritime-accent/10 rounded-lg flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-maritime-accent" />
                  </div>
                  <h3 className="text-lg font-semibold text-maritime-primary mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-20 bg-gradient-to-b from-white to-maritime-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-maritime-primary mb-4">
                Submit Your Application
              </h2>
              <p className="text-lg text-gray-600">
                Ready to join our team? Complete the application form below and we'll be in touch.
              </p>
            </div>

            <Card className="border-2 border-maritime-accent/20 shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        required
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        required
                        placeholder="+64 XX XXX XXXX"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="position">Position Applying For *</Label>
                      <Select
                        value={formData.position}
                        onValueChange={(value) => setFormData({ ...formData, position: value })}
                        required
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select position" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Marine Surveyor">Marine Surveyor</SelectItem>
                          <SelectItem value="Junior Marine Surveyor">Junior Marine Surveyor</SelectItem>
                          <SelectItem value="Technical Inspector">Technical Inspector</SelectItem>
                          <SelectItem value="Marine Engineering Consultant">Marine Engineering Consultant</SelectItem>
                          <SelectItem value="Other">Other Position</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="experience">Years of Experience *</Label>
                      <Input
                        id="experience"
                        type="text"
                        required
                        placeholder="e.g., 5 years"
                        value={formData.experience}
                        onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="location">Current Location *</Label>
                      <Input
                        id="location"
                        type="text"
                        required
                        placeholder="City, Country"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="resume">Resume/CV Link</Label>
                    <Input
                      id="resume"
                      type="url"
                      placeholder="Link to your resume (Google Drive, Dropbox, etc.)"
                      value={formData.resume}
                      onChange={(e) => setFormData({ ...formData, resume: e.target.value })}
                      className="mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Please provide a shareable link to your resume
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="coverLetter">Cover Letter / Why You Want to Join *</Label>
                    <Textarea
                      id="coverLetter"
                      required
                      placeholder="Tell us about your maritime experience, qualifications, and why you'd like to join Gamma Marine..."
                      rows={6}
                      value={formData.coverLetter}
                      onChange={(e) => setFormData({ ...formData, coverLetter: e.target.value })}
                      className="mt-2"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={formLoading}
                    className="w-full bg-maritime-accent hover:bg-maritime-accent/90 text-white"
                    size="lg"
                  >
                    {formLoading ? 'Submitting...' : (
                      <>
                        Submit Application
                        <Mail className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-8 text-center">
              <p className="text-gray-600">
                Questions about careers at Gamma Marine?{' '}
                <Link to="/contact" className="text-maritime-accent hover:text-maritime-accent/80 font-medium">
                  Contact our team
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
